import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { WelcomeConfetti } from '../../components/WelcomeConfetti';
import { ShiftMap } from '../../components/ShiftMap';
import axios from 'axios';
import {
  Calendar, Clock, CheckCircle2, AlertCircle, MapPin,
  ArrowRight, ClipboardList, TrendingUp, Video, Bell, FileText, Target, CalendarDays, ArrowLeftRight,
  ChevronDown, ChevronUp, Navigation, Radio, MessageSquare, Send
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { subscribeToPushNotifications } from '../../utils/pushNotifications';
import CaregiverProfilePhoto from '../../components/CaregiverProfilePhoto';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function CaregiverDashboard() {
  const { user } = useAuth();
  const [showWelcome, setShowWelcome] = useState(false);
  const [shifts, setShifts] = useState([]);
  const [availableShifts, setAvailableShifts] = useState([]);
  const [bidMessage, setBidMessage] = useState({});
  const [biddingShift, setBiddingShift] = useState(null);
  const [myBids, setMyBids] = useState(new Set());
  const navigate = useNavigate();

  useEffect(() => {
    subscribeToPushNotifications().catch(() => {});
    const hasSeenWelcome = localStorage.getItem('caregiver_welcome_seen');
    if (!hasSeenWelcome && user?.role === 'caregiver') {
      setShowWelcome(true);
      localStorage.setItem('caregiver_welcome_seen', 'true');
    }

    fetchShifts();
  }, [user]);

  const fetchShifts = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/shifts`, { withCredentials: true });
      const myShifts = data.filter(s => s.caregiver_id === user?.id);
      const available = data.filter(s => s.status === 'open');
      setShifts(myShifts);
      setAvailableShifts(available);
    } catch (err) {
      console.error('Failed to fetch shifts:', err);
    }
  };

  const handleAcceptShift = async (shiftId) => {
    try {
      await axios.put(`${API_URL}/api/shifts/${shiftId}/assign`, {}, { withCredentials: true });
      toast.success('Shift accepted');
      fetchShifts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept shift');
    }
  };

  const handleBidShift = async (shiftId) => {
    setBiddingShift(shiftId);
    try {
      await axios.post(`${API_URL}/api/shifts/${shiftId}/bid`, {
        message: (bidMessage[shiftId] || '').trim(),
      }, { withCredentials: true });
      toast.success('Bid submitted! You will be notified when the admin decides.');
      setMyBids(prev => new Set([...prev, shiftId]));
      setBidMessage(prev => ({ ...prev, [shiftId]: '' }));
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to submit bid';
      if (detail.includes('already bid')) {
        setMyBids(prev => new Set([...prev, shiftId]));
      }
      toast.error(detail);
    } finally { setBiddingShift(null); }
  };

  const handleWithdrawBid = async (shiftId) => {
    try {
      await axios.delete(`${API_URL}/api/shifts/${shiftId}/bid`, { withCredentials: true });
      toast.success('Bid withdrawn');
      setMyBids(prev => { const next = new Set(prev); next.delete(shiftId); return next; });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to withdraw bid');
    }
  };

  const upcomingShifts = shifts.filter(s => s.status === 'assigned');
  const completedShifts = shifts.filter(s => s.status === 'completed');
  const [expandedShift, setExpandedShift] = useState(null);

  return (
    <DashboardLayout>
      <WelcomeConfetti show={showWelcome} onComplete={() => setShowWelcome(false)} />

      <div className="space-y-8">
        {/* Welcome Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
              Hello, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-muted-foreground mt-1">
              Staff #{user?.staff_number} • Ready to make a difference today
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="border-border text-foreground hover:bg-muted gap-2"
              onClick={() => navigate('/video')}
              data-testid="caregiver-video-btn"
            >
              <Video className="w-4 h-4 text-[#FF6F61]" />Video Call
            </Button>
            <Button
              variant="outline"
              className="border-border text-foreground hover:bg-muted"
              onClick={async () => {
                const ok = await subscribeToPushNotifications();
                toast(ok ? 'Push notifications enabled' : 'Could not enable notifications');
              }}
              data-testid="caregiver-notifications-btn"
            >
              <Bell className="w-4 h-4 text-[#D97757]" />
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-6">
          <Card className="glass-card border-border">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">Upcoming Shifts</p>
                  <p className="text-3xl font-heading font-bold text-[#FF6F61] mt-2">
                    {upcomingShifts.length}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-[#FF6F61]/10">
                  <Calendar className="w-5 h-5 text-[#FF6F61]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-border">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">Available Shifts</p>
                  <p className="text-3xl font-heading font-bold text-[#A8E6CF] mt-2">
                    {availableShifts.length}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-[#A8E6CF]/10">
                  <Clock className="w-5 h-5 text-[#A8E6CF]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-border">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">Completed</p>
                  <p className="text-3xl font-heading font-bold text-foreground mt-2">
                    {completedShifts.length}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-muted">
                  <CheckCircle2 className="w-5 h-5 text-muted-foreground" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Available Shifts */}
        <Card className="glass-card border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="text-lg font-heading text-foreground">Available Shifts</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {availableShifts.length === 0 ? (
              <div className="p-8 text-center">
                <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No available shifts at the moment</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {availableShifts.map((shift) => {
                  const isExpanded = expandedShift === `avail-${shift.id}`;
                  const isBroadcast = shift.broadcast_status === 'broadcast';
                  const hasBid = myBids.has(shift.id);
                  return (
                    <div key={shift.id} className={`hover:bg-muted/50 transition-colors ${isBroadcast ? 'border-l-2 border-l-[#FF6F61]' : ''}`}>
                      <div className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${isBroadcast ? 'bg-[#FF6F61]/15' : 'bg-[#FF6F61]/10'}`}>
                            {isBroadcast ? <Radio className="w-6 h-6 text-[#FF6F61]" /> : <Calendar className="w-6 h-6 text-[#FF6F61]" />}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-foreground font-medium">{shift.client_name || 'Client'}</p>
                              {isBroadcast && (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${shift.broadcast_urgency === 'urgent' ? 'bg-red-500/20 text-red-400' : 'bg-[#FF6F61]/15 text-[#FF6F61]'}`}>
                                  {shift.broadcast_urgency === 'urgent' ? 'URGENT' : 'BROADCAST'}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{shift.date} &bull; {shift.start_time} - {shift.end_time}</p>
                            {shift.address && (
                              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                <MapPin className="w-3 h-3 text-[#FF6F61]" />{shift.address}
                              </p>
                            )}
                            {shift.broadcast_message && (
                              <p className="text-xs text-[#FF6F61]/80 mt-1 flex items-center gap-1">
                                <MessageSquare className="w-3 h-3" /> {shift.broadcast_message}
                              </p>
                            )}
                            {shift.notes && (
                              <p className="text-xs text-muted-foreground mt-1">{shift.notes}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <div className="flex items-center gap-2">
                            {shift.latitude && shift.longitude && (
                              <Button variant="ghost" size="sm" className="text-muted-foreground"
                                onClick={() => setExpandedShift(isExpanded ? null : `avail-${shift.id}`)}
                                data-testid={`toggle-map-avail-${shift.id}`}>
                                <Navigation className="w-4 h-4" />
                              </Button>
                            )}
                            {!isBroadcast ? (
                              <Button onClick={() => handleAcceptShift(shift.id)} className="btn-primary" data-testid={`accept-shift-${shift.id}`}>
                                Accept Shift
                              </Button>
                            ) : hasBid ? (
                              <Button variant="outline" size="sm" onClick={() => handleWithdrawBid(shift.id)}
                                className="text-xs border-muted-foreground/30"
                                data-testid={`withdraw-bid-${shift.id}`}>
                                Withdraw Bid
                              </Button>
                            ) : (
                              <Button onClick={() => handleBidShift(shift.id)}
                                className="bg-[#FF6F61] hover:bg-[#e55a4d] text-white"
                                disabled={biddingShift === shift.id}
                                data-testid={`bid-shift-${shift.id}`}>
                                {biddingShift === shift.id ? 'Submitting...' : 'Bid for Shift'}
                              </Button>
                            )}
                          </div>
                          {isBroadcast && !hasBid && (
                            <div className="flex gap-1 w-full sm:w-auto">
                              <input
                                type="text"
                                value={bidMessage[shift.id] || ''}
                                onChange={e => setBidMessage(prev => ({ ...prev, [shift.id]: e.target.value }))}
                                placeholder="Optional message..."
                                className="flex-1 rounded-md border border-border/50 bg-black/20 px-2 py-1 text-xs text-foreground focus:outline-none min-w-[150px]"
                                data-testid={`bid-message-${shift.id}`}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                      {isExpanded && shift.latitude && shift.longitude && (
                        <div className="px-4 pb-4" data-testid={`map-avail-${shift.id}`}>
                          <ShiftMap latitude={shift.latitude} longitude={shift.longitude} address={shift.address} height="200px" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* My Upcoming Shifts */}
        <Card className="glass-card border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="text-lg font-heading text-foreground">My Upcoming Shifts</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {upcomingShifts.length === 0 ? (
              <div className="p-8 text-center">
                <ClipboardList className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No upcoming shifts scheduled</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {upcomingShifts.map((shift) => {
                  const isExpanded = expandedShift === `my-${shift.id}`;
                  return (
                    <div key={shift.id} className="hover:bg-muted/50 transition-colors">
                      <div className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-[#A8E6CF]/10 flex items-center justify-center flex-shrink-0">
                            <CheckCircle2 className="w-6 h-6 text-[#A8E6CF]" />
                          </div>
                          <div>
                            <p className="text-foreground font-medium">{shift.client_name || 'Client'}</p>
                            <p className="text-sm text-muted-foreground">{shift.date} &bull; {shift.start_time} - {shift.end_time}</p>
                            {shift.address && (
                              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                <MapPin className="w-3 h-3 text-[#FF6F61]" />{shift.address}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {shift.latitude && shift.longitude && (
                            <Button variant="ghost" size="sm" className="text-muted-foreground"
                              onClick={() => setExpandedShift(isExpanded ? null : `my-${shift.id}`)}
                              data-testid={`toggle-map-my-${shift.id}`}>
                              <Navigation className="w-4 h-4 mr-1" />
                              <span className="text-xs">Map</span>
                            </Button>
                          )}
                          <span className="badge badge-success">Assigned</span>
                        </div>
                      </div>
                      {isExpanded && shift.latitude && shift.longitude && (
                        <div className="px-4 pb-4" data-testid={`map-my-${shift.id}`}>
                          <ShiftMap latitude={shift.latitude} longitude={shift.longitude} address={shift.address} height="220px" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Your Details */}
        <Card className="glass-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-foreground">Your Profile</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <CaregiverProfilePhoto />
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Staff Number</p>
              <p className="text-[#FF6F61] font-mono">{user?.staff_number}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">USIC</p>
              <p className="text-foreground font-mono">{user?.usic}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Email</p>
              <p className="text-foreground">{user?.email}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Company CISC</p>
              <p className="text-foreground font-mono">{user?.cisc}</p>
            </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid sm:grid-cols-2 gap-4">
          <Card className="glass-card border-border card-hover cursor-pointer"
            onClick={() => navigate('/timesheets')} data-testid="caregiver-timesheets-link">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-[#A8E6CF]/10">
                <FileText className="w-6 h-6 text-[#A8E6CF]" />
              </div>
              <div className="flex-1">
                <h3 className="text-foreground font-medium">My Timesheets</h3>
                <p className="text-sm text-muted-foreground">View hours & payslips</p>
              </div>
              <ArrowRight className="w-5 h-5 text-muted-foreground" />
            </CardContent>
          </Card>
          <Card className="glass-card border-border card-hover cursor-pointer"
            onClick={() => navigate('/wellness-goals')} data-testid="caregiver-wellness-link">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-blue-500/10">
                <Target className="w-6 h-6 text-blue-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-foreground font-medium">Wellness Goals</h3>
                <p className="text-sm text-muted-foreground">Track client progress</p>
              </div>
              <ArrowRight className="w-5 h-5 text-muted-foreground" />
            </CardContent>
          </Card>
          <Card className="glass-card border-border card-hover cursor-pointer"
            onClick={() => navigate('/availability')} data-testid="caregiver-availability-link">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-[#00A6A0]/10">
                <CalendarDays className="w-6 h-6 text-[#00A6A0]" />
              </div>
              <div className="flex-1">
                <h3 className="text-foreground font-medium">My Availability</h3>
                <p className="text-sm text-muted-foreground">Set weekly schedule</p>
              </div>
              <ArrowRight className="w-5 h-5 text-muted-foreground" />
            </CardContent>
          </Card>
          <Card className="glass-card border-border card-hover cursor-pointer"
            onClick={() => navigate('/shift-swaps')} data-testid="caregiver-shift-swaps-link">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-amber-500/10">
                <ArrowLeftRight className="w-6 h-6 text-amber-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-foreground font-medium">Shift Swaps</h3>
                <p className="text-sm text-muted-foreground">Request a shift swap</p>
              </div>
              <ArrowRight className="w-5 h-5 text-muted-foreground" />
            </CardContent>
          </Card>
          <Card className="glass-card border-border card-hover cursor-pointer"
            onClick={() => navigate('/clinical-logs')} data-testid="caregiver-clinical-logs-link">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-[#8B5CF6]/10">
                <FileText className="w-6 h-6 text-[#8B5CF6]" />
              </div>
              <div className="flex-1">
                <h3 className="text-foreground font-medium">Clinical Logs</h3>
                <p className="text-sm text-muted-foreground">Medications, vitals, wound care</p>
              </div>
              <ArrowRight className="w-5 h-5 text-muted-foreground" />
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default CaregiverDashboard;
